import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select, { InputActionMeta, MultiValue, StylesConfig, components } from "react-select";
import { Option } from "../../interface/Option";
import { InsuranceAxiosInstance } from "../../Network/InsuranceAxiosInstance";

type LinkedOption = Option & {
  __inactive?: boolean;
  isInactive?: boolean;
  is_active?: boolean | string | number | null;
  status?: string;
  is_deleted?: boolean;
  dbId?: number;
  [key: string]: any;
};

interface PolciyPlansSelectorProps {
  onChange: (selected: Option[]) => void;
  clear?: boolean;
  defaultValue?: Option[];
  value?: Option[];
  qaIdPrefix?: string;
  [key: string]: any;
}

const DEFAULT_QA_PREFIX = "policy-plan-selector";
const PAGE_LIMIT = 100;
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 350;
const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const qaAttrs = (prefix: string, suffix: string | number) => {
  const id = `${qaSafe(prefix)}-${qaSafe(suffix)}`;
  return { id, "data-testid": id, "data-qa": id };
};

const isInactive = (item: any): boolean => {
  if (!item || typeof item !== "object") return false;
  if (item.__inactive === true || item.isInactive === true || item.is_deleted === true) return true;
  const status = item.is_active ?? item.Is_active ?? item.active ?? item.status ?? item.Status;
  return status === false || status === 0 || status === "0" ||
    ["false", "inactive", "disabled"].includes(String(status ?? "").toLowerCase());
};
const optionId = (item: any) => String(item?.value ?? item?.id ?? "");
const extractRows = (response: any): any[] => {
  const data = response?.data;
  const raw = data?.result ?? data?.data ?? data?.plans ?? data;
  if (Array.isArray(raw)) return raw;
  for (const key of ["result", "data", "rows", "getInsurancePlans", "getInsurancePlan"]) {
    if (Array.isArray(raw?.[key])) return raw[key];
  }
  return [];
};
const normalizeRows = (rows: any[], inactiveByFilter: boolean): LinkedOption[] =>
  rows.map((item: any) => {
    const id = item?.id ?? item?.plan_id ?? item?.Plan_id;
    const label = String(item?.plan_name ?? item?.Plan_name ?? item?.name ?? item?.planName ?? "").trim();
    if (id === undefined || id === null || !label) return null;
    const explicitInactive = isInactive(item);
    const rawStatus = item.is_active ?? item.Is_active ?? item.active ?? item.status ?? item.Status;
    const explicitlyActive = rawStatus === true || rawStatus === 1 || rawStatus === "1" ||
      ["true", "active", "enabled"].includes(String(rawStatus ?? "").toLowerCase());
    // Some backends ignore the inactive filter. Never turn an explicitly
    // active item gray solely because it appeared in that response.
    if (inactiveByFilter && explicitlyActive && !explicitInactive) return null;
    const inactive = explicitInactive || (inactiveByFilter && !explicitlyActive);
    return { ...item, value: String(id), dbId: Number(id), label,
      __inactive: inactive, isInactive: inactive, is_active: !inactive } as LinkedOption;
  }).filter((item): item is LinkedOption => item !== null);

// Read both states, so previously linked inactive records remain detectable.
// No module-wide cache: reopening Edit must not reuse obsolete status information.
const fetchCatalogue = async (active: boolean, query = ""): Promise<LinkedOption[]> => {
  const all: LinkedOption[] = [];
  for (let page = 1; page <= 100; page++) {
    const response = await InsuranceAxiosInstance.get("getInsurancePlan", {
      params: { page, limit: PAGE_LIMIT, active: String(active), is_active: String(active),
        is_deleted: "false", name: query || undefined, }
    });
    const rows = extractRows(response);
    all.push(...normalizeRows(rows, !active));
    const pagination = response?.data?.pagination ?? response?.data;
    const totalPages = Number(pagination?.totalPages ?? pagination?.total_pages);
    if ((Number.isFinite(totalPages) && totalPages > 0 && page >= totalPages) || rows.length < PAGE_LIMIT) break;
  }
  return all;
};

const normalizeSelected = (raw: Option[] | undefined, catalogue: LinkedOption[]): LinkedOption[] => {
  const byId = new Map(catalogue.map(item => [optionId(item), item]));
  return (Array.isArray(raw) ? raw : []).map(option => {
    const source = option as LinkedOption;
    const latest = byId.get(optionId(source));
    // A catalogue entry is authoritative, while parent supplied status remains
    // available when the API did not return this linked record.
    if (!latest) return { ...source, __inactive: isInactive(source) };
    return { ...source, __inactive: isInactive(latest), isInactive: isInactive(latest),
      is_active: latest.is_active, status: latest.status, dbId: source.dbId ?? latest.dbId,
      label: source.label || latest.label };
  });
};

const PolciyPlansSelector = ({ onChange, clear, defaultValue, value, qaIdPrefix = DEFAULT_QA_PREFIX }: PolciyPlansSelectorProps) => {
  const [catalogue, setCatalogue] = useState<LinkedOption[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<LinkedOption[]>(() => normalizeSelected(value ?? defaultValue, []));
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const sequence = useRef(0);
  const selectedRef = useRef<LinkedOption[]>(selectedOptions);
  selectedRef.current = selectedOptions;

  const load = useCallback(async (query = "") => {
    const request = ++sequence.current;
    setLoading(true);
    // An unavailable inactive endpoint must not hide active menu choices.
    const [active, inactive] = await Promise.all([
      fetchCatalogue(true, query).catch(error => { console.error("Plan active lookup failed", error); return [] as LinkedOption[]; }),
      fetchCatalogue(false, query).catch(error => { console.error("Plan inactive lookup failed", error); return [] as LinkedOption[]; })
    ]);
    if (request !== sequence.current) return;
    const byId = new Map<string, LinkedOption>();
    active.forEach(item => byId.set(optionId(item), item));
    inactive.forEach(item => byId.set(optionId(item), item));
    setCatalogue(Array.from(byId.values()));
    setLoading(false);
  }, []);

  useEffect(() => { void load(""); return () => { sequence.current += 1; }; }, [load]);
  useEffect(() => {
    const query = inputValue.trim();
    if (!query) return;
    if (query.length < MIN_SEARCH_LENGTH) return;
    const timer = setTimeout(() => { void load(query); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue, load]);
  useEffect(() => {
    const incoming = value ?? defaultValue;
    if (Array.isArray(incoming)) setSelectedOptions(normalizeSelected(incoming, catalogue));
    else if (!incoming) setSelectedOptions([]);
  }, [value, defaultValue, catalogue]);

  useEffect(() => { if (clear) {
    // Parent supplies value=[] when opening Add; do not retain previous Edit choices.
    if (!value && !defaultValue) setSelectedOptions([]);
    setInputValue("");
  } }, [clear, value, defaultValue]);

  const options = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    return catalogue.filter(item => !isInactive(item))
      .filter(item => true);
  }, [catalogue]);

  const handleChange = (selected: MultiValue<LinkedOption>) => {
    const proposed = [...selected];
    const locked = selectedRef.current.filter(item => isInactive(item) &&
      !proposed.some(choice => optionId(choice) === optionId(item)));
    const next = normalizeSelected([...proposed, ...locked], catalogue);
    selectedRef.current = next;
    setSelectedOptions(next);
    onChange(next);
  };

  const handleInputChange = (next: string, meta: InputActionMeta) => {
    if (meta.action === "input-change") setInputValue(next);
    return next;
  };

  const styles: StylesConfig<LinkedOption, true> = {
    multiValue: (base, props) => ({ ...base, backgroundColor: isInactive(props.data) ? "#e5e7eb" : "#007BFF",
      border: isInactive(props.data) ? "1px solid #cbd5e1" : "none", borderRadius: "5px" }),
    multiValueLabel: (base, props) => ({ ...base, color: isInactive(props.data) ? "#475569" : "#FFFFFF" }),
    multiValueRemove: (base, props) => ({ ...base, display: isInactive(props.data) ? "none" : "flex",
      color: "#FFFFFF", ":hover": { backgroundColor: "#0056b3", color: "#FFFFFF" } }),
    menu: base => ({ ...base, zIndex: 999999, pointerEvents: "auto" }),
    menuList: base => ({ ...base, maxHeight: 280, overflowY: "auto" }),
    option: base => ({ ...base, cursor: "pointer" }),
  };

  const Menu = (props: any) => <components.Menu {...props}
    innerProps={{ ...(props.innerProps || {}), ...qaAttrs(qaIdPrefix, "menu") }} />;
  const MultiValueLabel = (props: any) => <components.MultiValueLabel {...props}>
    {props.data.label}{isInactive(props.data) ? " (Inactive)" : ""}
  </components.MultiValueLabel>;
  const MultiValueRemove = (props: any) => isInactive(props.data)
    ? null : <components.MultiValueRemove {...props} />;

  return <div {...qaAttrs(qaIdPrefix, "wrapper")}>
    <Select<LinkedOption, true>
      menuPosition="absolute" menuPlacement="top" menuShouldScrollIntoView={false}
      captureMenuScroll={true} maxMenuHeight={280} isMulti isClearable={false}
      closeMenuOnSelect={false} blurInputOnSelect={false}
      inputId={qaAttrs(qaIdPrefix, "input").id}
      instanceId={qaAttrs(qaIdPrefix, "select").id}
      name={qaAttrs(qaIdPrefix, "select").id}
      aria-label="Policy Plan selector"
      data-testid={qaAttrs(qaIdPrefix, "select").id}
      data-qa={qaAttrs(qaIdPrefix, "select").id}
      value={selectedOptions} onChange={handleChange} options={options}
      isLoading={loading} onInputChange={handleInputChange}
      placeholder="Select Plan..."
      noOptionsMessage={() => loading ? "Loading..." : "No active plans found"}
      styles={styles} components={{ Menu, MultiValueLabel, MultiValueRemove }}
    />
  </div>;
};

export default PolciyPlansSelector;
